<template>
  <div 
    class="video-file-status" 
    :class="{ 'clickable': props.clickable }" 
    @click="handleClick"
  >
    <div class="status-header">
      <font-awesome-icon icon="file-video" size="lg" />
      <span v-if="videoFile?.file_nickname" class="file-name" :title="videoFile?.file_name">{{ videoFile?.file_nickname }}</span>
      <span v-else class="file-name" :title="videoFile?.file_name">{{ videoFile?.file_name }}</span>
    </div>
    <div v-if="videoFile?.stream_status" class="stream-status">
      <span class="status-label">Stream Status: </span>
      <VideoFileStreamStatus :videoFile="videoFile" />
    </div>
    <div v-if="videoFile" class="status-details">
      <span>{{ getFormatDisplay(videoFile.format) || 'Unknown' }}</span>
    </div>
    <div class="status-details">
      <div v-if="videoFile?.duration" class="detail-item" title="Duration">
        <font-awesome-icon icon="clock" size="sm" />
        <span>{{ formatDuration(videoFile.duration) || 'Unknown' }}</span>
      </div>
      <div v-if="videoFile?.file_resolution" class="detail-item" title="Resolution">
        <font-awesome-icon icon="expand" size="sm" />
        <span>{{ videoFile.file_resolution }}</span>
      </div>
      <div v-if="videoFile?.file_framerate" class="detail-item" title="Framerate">
        <font-awesome-icon icon="tachometer-alt" size="sm" />
        <span>{{ videoFile.file_framerate }} fps</span>
      </div>
      <div v-if="videoFile?.file_format" class="detail-item" title="Codec">
        <font-awesome-icon icon="cube" size="sm" />
        <span>{{ videoFile.file_format }}</span>
      </div>
      <div v-else-if="videoFile?.file_codec_id" class="detail-item" title="Codec">
        <font-awesome-icon icon="cube" size="sm" />
        <span>{{ videoFile.file_codec_id }}</span>
      </div>
      <div v-if="videoFile?.file_size" class="detail-item" title="Size">
        <font-awesome-icon icon="file" size="sm" />
        <span>{{ formatFileSize(videoFile.file_size) }}</span>
      </div>
      <div v-if="videoFile?.file_bit_rate" class="detail-item" title="Bitrate">
        <font-awesome-icon icon="chart-line" size="sm" />
        <span>{{ formatBitrate(videoFile.file_bit_rate) }}</span>
      </div>
      <div v-if="videoFile?.file_bit_depth > 8" class="detail-item" title="Bit Depth">
        <span class="label hdr">HDR</span>
        <span>({{ videoFile.file_bit_depth }}-bit)</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@/scripts/icons';
import { formatDuration, formatFileSize, handleApiError, formatBitrate } from '@/scripts/shared-utils';
import { getFormatDisplay } from '@/scripts/shared-video';
import VideoFileStreamStatus from './VideoFileStreamStatus.vue';
import { getVideoFile, videoFileKeys } from '@/scripts/api/videoFileApi';

const props = defineProps({
  clickable: {
    type: Boolean,
    default: false
  },
  videoFileId: {
    type: String,
    default: null
  },
  videoFile: {
    type: Object,
    default: null
  }
});

const { data: fetchedVideoFile } = useQuery({
  queryKey: videoFileKeys.getVideoFile(props.videoFileId),
  queryFn: () => getVideoFile(props.videoFileId),
  enabled: !!props.videoFileId && !props.videoFile,
  staleTime: 60000, // One minute
  onError: (error) => handleApiError(error, 'fetching video file')
});

// Computed property to determine which video file to use
// Props take precedence over fetched data
const videoFile = computed(() => {
  return props.videoFile || fetchedVideoFile.value;
});

const router = useRouter();

const handleClick = () => {
  if (props.clickable && videoFile.value?.id) {
    router.push(`/videofiles/${videoFile.value.id}`);
  }
};
</script>

<style>
@import '@/styles/video-file.css';
</style>
